import { useEffect, type ReactNode } from "react";

import { useRouter } from "next/router";

import { PostHogProvider as PHProvider } from "posthog-js/react";

import { useCurrentAccount } from "@/hooks/use-current-account";
import { useNewUi } from "@/hooks/use-new-ui";
import { syncNewSystemCohort } from "@/lib/new-system-sync";
import { initPostHog, posthog } from "@/lib/posthog";
import {
  refreshPostHogContext,
  schedulePostHogFullRefresh,
} from "@/lib/posthog-enrichment";

const TOKEN_KEY = "TOKEN_LOGIN";

interface PostHogProviderProps {
  children: ReactNode;
}

function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Registra super props do teste A/B da UI nova em todos os eventos:
 * - ui_variant: "new" | "legacy" — modo de UI que o usuário está vendo
 * - ab_manager_new_ui: true só para legacy sorteado no tratamento
 * Precisa viver DENTRO do PHProvider (useNewUi lê a flag via posthog-js/react).
 */
function NewUiSuperProps() {
  const account = useCurrentAccount();
  const { isNewUi, cohortNewUi, isAbTreatment, isLoading } = useNewUi();

  useEffect(() => {
    if (isLoading) return;
    posthog.register({
      ui_variant: isNewUi ? "new" : "legacy",
      ab_manager_new_ui: isAbTreatment,
    });
  }, [isNewUi, isAbTreatment, isLoading]);

  useEffect(() => {
    if (isLoading) return;
    // O provider monta antes de Settings hidratar em contas legacy. O backend
    // resolve a conta pelo token; `world` é necessário apenas para o group do PostHog.
    void syncNewSystemCohort(account?.world, cohortNewUi);
  }, [account?.world, cohortNewUi, isLoading]);

  return null;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  const router = useRouter();

  useEffect(() => {
    initPostHog();
    const token = getToken();
    if (token) {
      void refreshPostHogContext({ token, mode: "sync" });
      schedulePostHogFullRefresh({ token });
    }
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const token = getToken();
      if (token) {
        void refreshPostHogContext({ token, mode: "sync" });
        schedulePostHogFullRefresh({ token });
      }
      posthog.capture("$pageview", { $current_url: url });
    };

    const token = getToken();
    if (token) {
      void refreshPostHogContext({ token, mode: "sync" });
      schedulePostHogFullRefresh({ token });
    }
    posthog.capture("$pageview", { $current_url: window.location.href });

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  return (
    <PHProvider client={posthog}>
      <NewUiSuperProps />
      {children}
    </PHProvider>
  );
}
